/**
 * Client-side image optimization before upload (F-30 assets): every image
 * over OPTIMIZE_MIN_BYTES is re-encoded locally — long edge capped at
 * UPLOAD_MAX_DIMENSION, recompressed as lossy WebP — so full-resolution
 * photos and bloated PNGs never leave the machine. Tiny images and GIFs
 * pass through byte-identical, and the original wins whenever the re-encode
 * fails to actually shrink it.
 */

/** Long-edge cap (px) applied client-side before an asset upload. */
export const UPLOAD_MAX_DIMENSION = 2048;

/** Files at or under this size upload untouched — recompressing them isn't worth it. */
export const OPTIMIZE_MIN_BYTES = 10 * 1024;

/** Lossy quality for the WebP (or fallback JPEG) re-encode. */
const LOSSY_QUALITY = 0.8;

// GIF is excluded: canvas re-encode would flatten animation to one frame.
// Anything else (svg, bmp, …) passes through for the server's 415 sniff.
const OPTIMIZABLE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

function encode(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Re-encode `file` for upload: long edge capped at UPLOAD_MAX_DIMENSION and
 * compressed as lossy WebP (alpha survives — lossy WebP carries an alpha
 * channel). Returns the ORIGINAL blob untouched when it is small, a
 * non-optimizable type, already smaller than the re-encode, or anything
 * fails — the server's own validation remains the source of truth.
 *
 * A re-encoded JPEG comes out EXIF-free with orientation baked into the
 * pixels (createImageBitmap applies it), so rotation survives the strip.
 */
export async function optimizeForUpload(file: Blob): Promise<Blob> {
  if (!OPTIMIZABLE_TYPES.has(file.type) || file.size <= OPTIMIZE_MIN_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // undecodable — let the server's magic-byte sniff decide
  }
  try {
    const scale = Math.min(1, UPLOAD_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (ctx === null) return file;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    let best = await encode(canvas, "image/webp", LOSSY_QUALITY);
    if (best === null || best.type !== "image/webp") {
      // No webp encoder (older Safari falls back to png) — re-encode in the
      // original format instead: jpeg goes lossy, png stays lossless so
      // transparency is never flattened. blob.type reflects the real
      // encoding, and uploadAsset sends blob.type, so declared and actual
      // types always agree.
      const quality = file.type === "image/png" ? undefined : LOSSY_QUALITY;
      best = await encode(canvas, file.type, quality);
    }
    if (best === null) return file;
    // Efficiency guard: ship whichever is smaller. An over-cap image is the
    // one exception — the dimension cap wins even on a rare size regression.
    if (best.size >= file.size && scale === 1) return file;
    return best;
  } finally {
    bitmap.close();
  }
}
